import { Navigate, Outlet, useLocation } from 'react-router-dom'
import {
  defaultLandingPathForDesk,
  isDocumentFilingOnlyPathAllowed,
  isPathAllowedOnDesk,
  isThinAdminPathAllowed,
  THIN_ADMIN_LANDING_PATH,
} from '@/config/workspace-desk-routes'
import { resolveWorkspaceDeskFromHost } from '@/config/workspace-desk'
import { AppPaths } from '@/config/app-paths'
import { useMeSessionQuery } from '@/features/auth/hooks/use-me-session-query'
import { isDocumentFilingOnly, isThinAdmin } from '@/shared/lib/staff-capabilities'
import { AuthLoadingScreen } from '@/features/auth/components/require-auth'

/**
 * Redirects desk subdomain users away from routes outside their portal
 * (e.g. pro.amglobal.co → PRO only). Plain Administrators (thin admin) are
 * locked to the thin allowlist on every host, including localhost.
 */
export function WorkspaceDeskRouteGuard() {
  const location = useLocation()
  const session = useMeSessionQuery()
  const desk = resolveWorkspaceDeskFromHost(window.location.hostname)
  const path = location.pathname

  // Placeholder session has empty roles — wait for the real /me/session so we
  // do not briefly allow full access for a thin Administrator.
  if (session.isPlaceholderData || (session.isPending && session.data === undefined)) {
    return <AuthLoadingScreen />
  }

  const roles = session.data?.roles ?? []
  const thinAdmin = isThinAdmin(roles) && desk !== 'hr'
  const filingOnly = isDocumentFilingOnly(session.data?.capabilities ?? [])

  if (filingOnly) {
    const landing = AppPaths.documentFilingOutstanding
    if (path === '/' || path === AppPaths.home || !isDocumentFilingOnlyPathAllowed(path)) {
      if (path === landing) return <Outlet />
      return <Navigate to={landing} replace state={{ deskRedirectFrom: path }} />
    }
    return <Outlet />
  }

  if (thinAdmin) {
    const landing = THIN_ADMIN_LANDING_PATH
    if (path === '/' || path === AppPaths.home) {
      return <Navigate to={landing} replace />
    }
    if (!isThinAdminPathAllowed(path)) {
      return <Navigate to={landing} replace state={{ deskRedirectFrom: path }} />
    }
    return <Outlet />
  }

  if (!desk) {
    return <Outlet />
  }

  const landing = defaultLandingPathForDesk(
    desk,
    roles,
    session.data?.capabilities ?? [],
  )

  if (path === '/' || path === AppPaths.home) {
    // SystemAdministrator on admin desk may keep the home dashboard.
    if (desk === 'admin') {
      return <Outlet />
    }
    return <Navigate to={landing} replace />
  }

  if (!isPathAllowedOnDesk(desk, path)) {
    return <Navigate to={landing} replace state={{ deskRedirectFrom: path }} />
  }

  return <Outlet />
}
